<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'dashboard/composables/useI18n';
import { useRoute } from 'dashboard/composables/route';
import { useEmitter } from 'dashboard/composables/emitter';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  isAConversationRoute,
  isAInboxViewRoute,
  isNotificationRoute,
} from 'dashboard/helper/routeHelpers';
import { useEventListener } from '@vueuse/core';

const { t } = useI18n();
const route = useRoute();

const RECONNECTED_BANNER_TIMEOUT = 2000;
// Don't surface the banner for brief websocket blips. Only show it if we are
// still disconnected after this delay; instant reconnects stay silent.
const DISCONNECT_DEBOUNCE = 4000;

const showNotification = ref(!navigator.onLine);
const isDisconnected = ref(false);
const isReconnecting = ref(false);
const isReconnected = ref(false);
let reconnectTimeout = null;
let disconnectDebounceTimer = null;

const clearDisconnectDebounce = () => {
  clearTimeout(disconnectDebounceTimer);
  disconnectDebounceTimer = null;
};

const bannerText = computed(() => {
  if (isReconnecting.value) return t('NETWORK.NOTIFICATION.RECONNECTING');
  if (isReconnected.value) return t('NETWORK.NOTIFICATION.RECONNECT_SUCCESS');
  return t('NETWORK.NOTIFICATION.OFFLINE');
});

const bannerDetail = computed(() => {
  if (isReconnected.value) return '';
  if (isReconnecting.value)
    return t('NETWORK.NOTIFICATION.RECONNECTING_DETAIL');
  return t('NETWORK.NOTIFICATION.OFFLINE_DETAIL');
});

// Ámbar = sin conexión, azul = reconectando, verde = volvió. Antes era
// siempre un amarillo apagado con solo "Desconectado" y dos íconos sueltos.
const tone = computed(() => {
  if (isReconnected.value) return 'success';
  if (isReconnecting.value) return 'info';
  return 'warning';
});

const TONE_CLASSES = {
  warning:
    'bg-amber-50 text-amber-900 ring-amber-200 dark:bg-amber-900/80 dark:text-amber-50 dark:ring-amber-700',
  info: 'bg-sky-50 text-sky-900 ring-sky-200 dark:bg-sky-900/80 dark:text-sky-50 dark:ring-sky-700',
  success:
    'bg-green-50 text-green-900 ring-green-200 dark:bg-green-900/80 dark:text-green-50 dark:ring-green-700',
};

const iconName = computed(() => (isReconnected.value ? 'wifi' : 'wifi-off'));
const canRefresh = computed(
  () => !isReconnecting.value && !isReconnected.value
);

const refreshPage = () => {
  window.location.reload();
};

const closeNotification = () => {
  showNotification.value = false;
  isReconnected.value = false;
  clearTimeout(reconnectTimeout);
  clearDisconnectDebounce();
};

const isInAnyOfTheRoutes = routeName => {
  return (
    isAConversationRoute(routeName, true) ||
    isAInboxViewRoute(routeName, true) ||
    isNotificationRoute(routeName, true)
  );
};

const updateWebsocketStatus = () => {
  isDisconnected.value = true;
  // Debounce: only surface the banner if we are still disconnected once the
  // window elapses. A reconnect before then clears this timer (see below).
  if (disconnectDebounceTimer) return;
  disconnectDebounceTimer = setTimeout(() => {
    disconnectDebounceTimer = null;
    if (isDisconnected.value) {
      showNotification.value = true;
    }
  }, DISCONNECT_DEBOUNCE);
};

const handleReconnectionCompleted = () => {
  // Relies on WEBSOCKET_RECONNECT always firing before RECONNECT_COMPLETED
  // (ReconnectService emits COMPLETED only after handling RECONNECT), so a
  // pending debounce timer was already cleared by handleReconnecting.
  // Blip resolved before the banner ever surfaced — reset silently, no flash.
  if (disconnectDebounceTimer || !showNotification.value) {
    clearDisconnectDebounce();
    isDisconnected.value = false;
    isReconnecting.value = false;
    return;
  }
  isDisconnected.value = false;
  isReconnecting.value = false;
  isReconnected.value = true;
  showNotification.value = true;
  reconnectTimeout = setTimeout(closeNotification, RECONNECTED_BANNER_TIMEOUT);
};

const handleReconnecting = () => {
  // Still within the debounce window: the disconnect was never shown, so the
  // reconnect shouldn't flash "Reconnecting" either. Reset silently.
  if (disconnectDebounceTimer) {
    clearDisconnectDebounce();
    isDisconnected.value = false;
    return;
  }
  if (isInAnyOfTheRoutes(route.name)) {
    isReconnecting.value = true;
    isReconnected.value = false;
    showNotification.value = true;
  } else {
    handleReconnectionCompleted();
  }
};

const updateOnlineStatus = event => {
  // Case: Websocket is not disconnected
  // If the app goes offline, show the notification
  // If the app goes online, close the notification

  // Case: Websocket is disconnected
  // If the app goes offline, show the notification
  // If the app goes online but the websocket is disconnected, don't close the notification
  // If the app goes online and the websocket is not disconnected, close the notification

  if (event.type === 'offline') {
    showNotification.value = true;
  } else if (event.type === 'online' && !isDisconnected.value) {
    handleReconnectionCompleted();
  }
};

useEventListener('online', updateOnlineStatus);
useEventListener('offline', updateOnlineStatus);
useEmitter(BUS_EVENTS.WEBSOCKET_DISCONNECT, updateWebsocketStatus);
useEmitter(
  BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED,
  handleReconnectionCompleted
);
useEmitter(BUS_EVENTS.WEBSOCKET_RECONNECT, handleReconnecting);

onBeforeUnmount(() => {
  clearTimeout(reconnectTimeout);
  clearDisconnectDebounce();
});
</script>

<template>
  <transition name="network-notification-fade" tag="div">
    <div
      v-show="showNotification"
      class="fixed z-50 -translate-x-1/2 top-4 left-1/2 w-[min(28rem,calc(100vw-2rem))]"
      role="status"
      aria-live="polite"
    >
      <div
        class="flex items-start gap-3 px-3 py-2.5 rounded-lg shadow-lg ring-1"
        :class="TONE_CLASSES[tone]"
      >
        <fluent-icon :icon="iconName" size="18" class="flex-shrink-0 mt-0.5" />
        <div class="flex-1 min-w-0">
          <p class="m-0 text-sm font-semibold leading-5">{{ bannerText }}</p>
          <p v-if="bannerDetail" class="m-0 text-xs leading-4 opacity-80">
            {{ bannerDetail }}
          </p>
        </div>
        <woot-button
          v-if="canRefresh"
          size="tiny"
          variant="smooth"
          color-scheme="warning"
          icon="arrow-clockwise"
          class="flex-shrink-0"
          @click="refreshPage"
        >
          {{ $t('NETWORK.BUTTON.REFRESH') }}
        </woot-button>
        <woot-button
          v-tooltip="$t('NETWORK.BUTTON.CLOSE')"
          variant="clear"
          size="tiny"
          color-scheme="secondary"
          icon="dismiss"
          class="flex-shrink-0"
          @click="closeNotification"
        />
      </div>
    </div>
  </transition>
</template>
